// src/components/Projects/ProjectCard.jsx

import React from "react";
import { formatDistanceToNow } from "date-fns";
import { motion } from "framer-motion";
import { FaStar, FaCodeBranch, FaClock } from "react-icons/fa";

// Define language colors
const languageColors = {
  JavaScript: "#f1e05a",
  Python: "#3572A5",
  Java: "#b07219",
  HTML: "#e34c26",
  CSS: "#563d7c",
  Shell: "#89e051",
  Dockerfile: "#384d54",
  Kubernetes: "#326ce5",
  Vue: "#41b883",
  Go: "#00ADD8",
  PHP: "#4F5D95",
  AIDL: "#34B7F1",
  TypeScript: "#3178C6",
  // Add more languages as needed
};

const ProjectCard = React.memo(({ repo }) => {
  const isFeatured = ["vxdf", "VulnDroid", "Learning-Linux"].includes(
    repo.name,
  );

  return (
    <motion.a
      href={repo.html_url}
      target="_blank"
      rel="noopener noreferrer"
      className={`block relative overflow-hidden rounded-xl transition-all duration-300 ${
        isFeatured
          ? "bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-2 border-blue-400/30"
          : "bg-white/5 border border-white/10"
      } backdrop-blur-sm hover:bg-white/10 hover:border-white/20 hover:shadow-2xl hover:shadow-blue-500/10 group`}
      whileHover={{ y: -5, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      {/* Featured badge */}
      {isFeatured && (
        <div className="absolute top-0 right-0 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
          FEATURED
        </div>
      )}

      <div className="p-6">
        {/* Project Name */}
        <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
          {repo.name.toLowerCase() === "vxdf" ? "VXDF" : repo.name}
        </h2>

        {/* Description */}
        {repo.description && (
          <p className="text-gray-300 mb-4 line-clamp-2 text-sm">
            {repo.description}
          </p>
        )}

        {/* Programming Languages */}
        <div className="flex flex-wrap items-center mb-4 gap-2">
          {repo.languages && Object.keys(repo.languages).length > 0 ? (
            Object.entries(repo.languages)
              .sort(([, a], [, b]) => b - a) // Sort by usage
              .slice(0, 3) // Show top 3 languages
              .map(([language]) => (
                <div
                  key={language}
                  className="flex items-center bg-white/10 rounded-full px-3 py-1"
                >
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{
                      backgroundColor: languageColors[language] || "#cccccc",
                    }}
                  />
                  <span className="text-xs text-gray-300">{language}</span>
                </div>
              ))
          ) : (
            <span className="text-xs text-gray-500">No language data</span>
          )}
        </div>

        {/* Stats */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center text-yellow-400 hover:text-yellow-300 transition-colors">
              <FaStar className="mr-1" />
              <span>{repo.stargazers_count}</span>
            </div>
            <div className="flex items-center text-blue-400 hover:text-blue-300 transition-colors">
              <FaCodeBranch className="mr-1" />
              <span>{repo.forks_count}</span>
            </div>
          </div>

          {/* Last Updated */}
          <div className="flex items-center text-gray-400 text-xs">
            <FaClock className="mr-1" />
            <span>{formatDistanceToNow(new Date(repo.updated_at))} ago</span>
          </div>
        </div>

        {/* Hover effect overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-blue-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
      </div>
    </motion.a>
  );
});

ProjectCard.displayName = "ProjectCard";

export default ProjectCard;
